/**
 * FieldMappingStep - Step 3 of SMB Vendor Cost Import
 *
 * Per-table field mapping with accordion UI.
 * Each table from vendor file gets its own accordion panel with:
 * - Standard field dropdowns (SKU, Cost, MAP, etc.)
 * - 5 sample data rows displayed inline
 * - Field locking (once mapped, locked across all tables)
 */

import React, { useState, useEffect } from 'react'
import { Space, Card, Button, Select, Typography, Collapse, Tag, Alert, Spin } from 'antd'
import { LockOutlined, EyeOutlined } from '@ant-design/icons'
import { errorService } from '../../../services/errorService'
import { smbSyncService } from '../../../services/smbSyncService'
import { saveCatalogMappings, loadCatalogMappings, saveVendorMappings, loadVendorMappings } from './smbMappingStorage'
import { SampleDataModal } from './SampleDataModal'
import type {
  StepProps,
  CatalogFieldMapping,
  VendorFieldType,
  CatalogFieldType,
  TableFieldMapping,
  VendorMappings,
  VendorSampleData,
} from './types'

const { Title, Text } = Typography

/**
 * Field definitions for catalog mapping
 */
const CATALOG_FIELDS: { type: CatalogFieldType; label?: string; required: boolean; description: string }[] = [
  { type: 'Unique ID', required: true, description: 'Primary identifier for each product' },
  { type: 'SKU', required: false, description: 'Stock Keeping Unit' },
  { type: 'Vendor Code', required: false, description: "Vendor's product code" },
  { type: 'GTIN', label: 'GTIN (UPC/EAN)', required: false, description: 'Global Trade Item Number' },
  { type: 'Price', required: true, description: 'Current selling price' },
  { type: 'Cost', required: true, description: 'Current cost value' },
]

/**
 * Field definitions for vendor mapping
 */
const VENDOR_FIELDS: { type: VendorFieldType; label?: string; required: boolean; description: string }[] = [
  { type: 'Manufacturer SKU', required: false, description: "Manufacturer's product identifier" },
  { type: 'Vendor SKU', required: false, description: "Vendor's product identifier" },
  { type: 'UPC', label: 'GTIN (UPC/EAN)', required: false, description: 'Global Trade Item Number' },
  { type: 'Cost', required: false, description: 'New cost from vendor' },
  { type: 'MAP', required: false, description: 'Minimum Advertised Price' },
  { type: 'MSRP', required: false, description: 'Manufacturer Suggested Retail Price' },
]

/**
 * Global field locking map
 * Tracks which vendor columns are locked to which standard fields across all tables
 * Key: vendor column name, Value: { fieldType, sourceTableId }
 */
type FieldLockMap = Record<string, { fieldType: VendorFieldType; sourceTableId: string }>

/**
 * Step 3: Map fields per table with accordion UI
 */
export const FieldMappingStep: React.FC<StepProps> = ({ state, onNext, onBack, onCancel }) => {
  const [catalogMappings, setCatalogMappings] = useState<CatalogFieldMapping[]>([])
  const [tableFieldMappings, setTableFieldMappings] = useState<TableFieldMapping[]>([])
  const [sampleData, setSampleData] = useState<VendorSampleData | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeKeys, setActiveKeys] = useState<string[]>([])
  const [fieldLocks, setFieldLocks] = useState<FieldLockMap>({})
  const [sampleModalVisible, setSampleModalVisible] = useState(false)
  const [sampleModalTableId, setSampleModalTableId] = useState<string | null>(null)

  // Initialize catalog mappings - try to load saved mappings first
  useEffect(() => {
    if (catalogMappings.length === 0) {
      const savedMappings = loadCatalogMappings(state.platform, state.catalogColumns)

      if (savedMappings) {
        setCatalogMappings(savedMappings)
      } else {
        setCatalogMappings(
          CATALOG_FIELDS.map((field) => ({
            fieldType: field.type,
            selectedColumns: [],
          }))
        )
      }
    }
  }, [state.platform, state.catalogColumns])

  // Fetch sample data when component mounts
  useEffect(() => {
    const fetchSampleData = async () => {
      if (!state.jobId) return

      setLoading(true)
      try {
        const response = await smbSyncService.getSampleData(state.jobId)

        if (!response.success || !response.data) {
          errorService.showError(
            'Failed to Load Sample Data',
            response.message || 'Could not fetch vendor file sample data',
            response.errors
          )
          return
        }

        setSampleData(response.data)
        const fetchedSampleData = response.data

        // Try to load saved vendor mappings first
        const vendorFilename = state.vendorFile?.name
        let savedVendorMappings: TableFieldMapping[] | null = null

        if (vendorFilename) {
          savedVendorMappings = loadVendorMappings(state.platform, vendorFilename, fetchedSampleData)
        }

        if (savedVendorMappings && savedVendorMappings.length > 0) {
          // Rebuild field locks from saved mappings (tables that have actual mappings)
          const rebuiltLocks: FieldLockMap = {}
          savedVendorMappings.forEach((tableMapping) => {
            Object.entries(tableMapping.mappings).forEach(([fieldType, columnName]) => {
              if (columnName && !rebuiltLocks[columnName]) {
                // First table to have this column mapped owns the lock
                rebuiltLocks[columnName] = {
                  fieldType: fieldType as VendorFieldType,
                  sourceTableId: tableMapping.tableId,
                }
              }
            })
          })

          // Apply existing locks to new tables that have matching columns
          // This handles the case where Tab1 has "Item Code" -> Manufacturer SKU,
          // and Tab2 is newly added and also has "Item Code" column
          const updatedMappings = savedVendorMappings.map((tableMapping) => {
            // Check if any columns in this table match existing locks
            const tableSample = fetchedSampleData.table_samples[tableMapping.tableId]
            if (!tableSample) return tableMapping

            const tableColumns = new Set(tableSample.header_values)
            let hasUpdates = false
            const newMappings = { ...tableMapping.mappings }

            // For each lock, check if this table has the column and doesn't have a mapping yet
            Object.entries(rebuiltLocks).forEach(([columnName, lock]) => {
              if (tableColumns.has(columnName)) {
                // This table has the locked column
                const currentValue = newMappings[lock.fieldType]
                if (currentValue === null) {
                  // No mapping yet - apply the lock
                  newMappings[lock.fieldType] = columnName
                  hasUpdates = true
                }
              }
            })

            return hasUpdates ? { ...tableMapping, mappings: newMappings } : tableMapping
          })

          setTableFieldMappings(updatedMappings)
          setFieldLocks(rebuiltLocks)

          // Auto-expand first table that needs attention (incomplete or first one)
          const firstIncomplete = updatedMappings.find(
            (t) => !Object.values(t.mappings).some((v) => v !== null)
          )
          setActiveKeys([firstIncomplete?.tableId || updatedMappings[0].tableId])
        } else {
          // Initialize fresh table field mappings
          const initialMappings: TableFieldMapping[] = []
          Object.keys(fetchedSampleData.table_samples).forEach((tableId) => {
            const sample = fetchedSampleData.table_samples[tableId]
            const tabName = findTabNameForTable(tableId, fetchedSampleData)

            // Skip tables with no data rows
            if (sample.total_data_rows === 0) {
              return
            }

            // Initialize empty mappings for all vendor fields
            const mappings: Record<VendorFieldType, string | null> = {
              'Manufacturer SKU': null,
              'Vendor SKU': null,
              'UPC': null,
              'Cost': null,
              'MAP': null,
              'MSRP': null,
              'N/A': null,
            }

            initialMappings.push({
              tableId,
              tabName,
              totalRows: sample.total_data_rows,
              mappings,
            })
          })

          setTableFieldMappings(initialMappings)

          // Auto-expand first incomplete table
          if (initialMappings.length > 0) {
            setActiveKeys([initialMappings[0].tableId])
          }
        }
      } catch (error: any) {
        errorService.showError(
          'Error Loading Sample Data',
          error.message || 'An unexpected error occurred',
          []
        )
      } finally {
        setLoading(false)
      }
    }

    fetchSampleData()
  }, [state.jobId, state.vendorFile?.name, state.platform, state.selectedTabs.join(',')])

  /**
   * Find the tab name for a given table ID
   */
  const findTabNameForTable = (tableId: string, data: VendorSampleData): string => {
    for (const [tabName, tableIds] of Object.entries(data.tables_by_tab)) {
      if (tableIds.includes(tableId)) {
        return tabName
      }
    }
    return tableId // Fallback to table ID if tab not found
  }

  /**
   * Handle catalog field mapping change
   */
  const handleCatalogMappingChange = (fieldType: CatalogFieldType, columns: string[]) => {
    setCatalogMappings((prev) =>
      prev.map((m) => (m.fieldType === fieldType ? { ...m, selectedColumns: columns } : m))
    )
  }

  /**
   * Handle vendor field mapping change for a specific table
   * When a column is mapped, also auto-populate the same mapping in other tables that have that column
   */
  const handleTableFieldMappingChange = (
    tableId: string,
    fieldType: VendorFieldType,
    vendorColumn: string | null
  ) => {
    // First, handle removing old lock if clearing a mapping
    setTableFieldMappings((prev) => {
      const currentTable = prev.find((t) => t.tableId === tableId)
      const oldColumn = currentTable?.mappings[fieldType]

      // If clearing a mapping that this table owns the lock for, remove the lock
      if (oldColumn && !vendorColumn) {
        const lock = fieldLocks[oldColumn]
        if (lock && lock.sourceTableId === tableId) {
          // This table owns the lock - remove it and clear from other tables
          setFieldLocks((locks) => {
            const newLocks = { ...locks }
            delete newLocks[oldColumn]
            return newLocks
          })

          // Clear the mapping from other tables that had it auto-populated
          return prev.map((table) => {
            if (table.mappings[fieldType] === oldColumn) {
              return { ...table, mappings: { ...table.mappings, [fieldType]: null } }
            }
            return table
          })
        }
      }

      // If setting a new mapping
      if (vendorColumn) {
        // Set the lock with this table as the source
        setFieldLocks((locks) => ({
          ...locks,
          [vendorColumn]: { fieldType, sourceTableId: tableId },
        }))

        // Update this table AND auto-populate in other tables that have this column
        return prev.map((table) => {
          const newMappings = { ...table.mappings }

          if (table.tableId === tableId) {
            // This is the source table - set the mapping
            newMappings[fieldType] = vendorColumn
          } else {
            // Check if this table has this column in its headers
            const tableSample = sampleData?.table_samples[table.tableId]
            if (tableSample?.header_values.includes(vendorColumn)) {
              // Auto-populate the same mapping (it will be locked)
              newMappings[fieldType] = vendorColumn
            }
          }

          return { ...table, mappings: newMappings }
        })
      }

      // Just clearing without setting new value
      return prev.map((table) => {
        if (table.tableId !== tableId) return table
        return { ...table, mappings: { ...table.mappings, [fieldType]: null } }
      })
    })
  }

  /**
   * Get available vendor columns for a specific table and field type
   * Excludes:
   * - Columns already mapped in this table (to other fields)
   * - Columns locked to a different field type by another table
   */
  const getAvailableColumns = (tableId: string, fieldType: VendorFieldType): string[] => {
    const tableSample = sampleData?.table_samples[tableId]
    if (!tableSample) return []

    const tableMapping = tableFieldMappings.find((t) => t.tableId === tableId)
    if (!tableMapping) return []

    // Get columns already mapped in this table (to other fields)
    const mappedInThisTable = new Set<string>()
    Object.entries(tableMapping.mappings).forEach(([field, column]) => {
      if (field !== fieldType && column) {
        mappedInThisTable.add(column)
      }
    })

    // Filter columns and sort alphabetically
    return tableSample.header_values
      .filter((column) => {
        // Exclude if already mapped to another field in this table
        if (mappedInThisTable.has(column)) return false

        // Check if column is locked to a different field type
        const lock = fieldLocks[column]
        if (lock && lock.fieldType !== fieldType) return false

        return true
      })
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  }

  /**
   * Check if a vendor column is locked for a specific table
   * A field is locked if:
   * - It was mapped by a DIFFERENT table (not this one)
   * - The source table still owns the lock
   */
  const isColumnLocked = (tableId: string, vendorColumn: string): boolean => {
    const lock = fieldLocks[vendorColumn]

    // Not locked if not in lock map
    if (!lock) return false

    // Locked only if a DIFFERENT table set this lock
    return lock.sourceTableId !== tableId
  }

  /**
   * Validate mappings
   */
  const validateMappings = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = []

    // Check catalog required fields
    const uniqueIdMapping = catalogMappings.find((m) => m.fieldType === 'Unique ID')
    const priceMapping = catalogMappings.find((m) => m.fieldType === 'Price')
    const costMapping = catalogMappings.find((m) => m.fieldType === 'Cost')

    if (!uniqueIdMapping?.selectedColumns.length) {
      errors.push('Catalog: Unique ID field is required')
    }
    if (!priceMapping?.selectedColumns.length) {
      errors.push('Catalog: Price field is required')
    }
    if (!costMapping?.selectedColumns.length) {
      errors.push('Catalog: Cost field is required')
    }

    // Check that at least one join key pair exists across all tables
    const catalogHasSku = catalogMappings.find((m) => m.fieldType === 'SKU')?.selectedColumns.length
    const catalogHasVendorCode = catalogMappings.find((m) => m.fieldType === 'Vendor Code')?.selectedColumns.length
    const catalogHasGtin = catalogMappings.find((m) => m.fieldType === 'GTIN')?.selectedColumns.length

    const hasManufacturerSku = tableFieldMappings.some((t) => t.mappings['Manufacturer SKU'] !== null)
    const hasVendorSku = tableFieldMappings.some((t) => t.mappings['Vendor SKU'] !== null)
    const hasUpc = tableFieldMappings.some((t) => t.mappings.UPC !== null)

    const hasValidJoinPair =
      (catalogHasSku && hasManufacturerSku) ||
      (catalogHasVendorCode && hasVendorSku) ||
      (catalogHasGtin && hasUpc)

    if (!hasValidJoinPair) {
      errors.push(
        'At least one join key pair must be mapped (e.g., SKU + Manufacturer SKU, or GTIN + UPC)'
      )
    }

    return { valid: errors.length === 0, errors }
  }

  /**
   * Consolidate per-table mappings into single VendorMappings structure
   */
  const consolidateMappings = (): VendorMappings => {
    const consolidated: VendorMappings = {
      Cost: [],
      MAP: [],
      MSRP: [],
      'Manufacturer SKU': [],
      'Vendor SKU': [],
      UPC: [],
    }

    tableFieldMappings.forEach((table) => {
      Object.entries(table.mappings).forEach(([fieldType, vendorColumn]) => {
        if (!vendorColumn) return

        const field = fieldType as VendorFieldType
        if (!consolidated[field]) {
          consolidated[field] = []
        }

        // Add to consolidated array if not already present
        if (!consolidated[field]!.includes(vendorColumn)) {
          consolidated[field]!.push(vendorColumn)
        }
      })
    })

    return consolidated
  }

  /**
   * Handle next step
   */
  const handleNext = () => {
    const { valid, errors } = validateMappings()

    if (!valid) {
      errorService.showError('Incomplete Mappings', 'Please fix the following issues:', [
        { code: 'VALIDATION_ERROR', message: errors.join('\n'), details: { errors } },
      ])
      return
    }

    // Save catalog mappings to localStorage for future use
    saveCatalogMappings(state.platform, catalogMappings)

    // Save vendor mappings to localStorage for future use (keyed by filename)
    if (state.vendorFile?.name) {
      saveVendorMappings(state.platform, state.vendorFile.name, tableFieldMappings)
    }

    // Consolidate per-table mappings
    const consolidatedVendorMappings = consolidateMappings()

    onNext({
      catalogMappings,
      tableFieldMappings,
      vendorSampleData: sampleData,
      // Pass consolidated mappings for the backend (cast to any since VendorMappings uses string[] not VendorFieldMapping[])
      vendorMappings: consolidatedVendorMappings as any,
    })
  }

  /**
   * Show sample data modal for a table
   */
  const handleShowSampleData = (tableId: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent accordion toggle
    setSampleModalTableId(tableId)
    setSampleModalVisible(true)
  }

  /**
   * Get the source table name for a locked column
   */
  const getLockSourceTableName = (vendorColumn: string): string => {
    const lock = fieldLocks[vendorColumn]
    if (!lock) return ''
    const sourceTable = tableFieldMappings.find((t) => t.tableId === lock.sourceTableId)
    return sourceTable?.tabName || lock.sourceTableId
  }


  /**
   * Build Collapse item for a single table
   */
  const buildTableItem = (tableMapping: TableFieldMapping) => {
    const tableSample = sampleData?.table_samples[tableMapping.tableId]
    if (!tableSample) return null

    return {
      key: tableMapping.tableId,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Space>
            <Text strong>
              {tableMapping.tableId}
            </Text>
            {tableMapping.tabName && tableMapping.tabName !== 'PDF' && (
              <Text type="secondary">({tableMapping.tabName})</Text>
            )}
            <Text type="secondary">
              {tableMapping.totalRows.toLocaleString()} rows
            </Text>
          </Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => handleShowSampleData(tableMapping.tableId, e)}
          >
            View Sample Data
          </Button>
        </div>
      ),
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {VENDOR_FIELDS.map((field) => {
            const currentMapping = tableMapping.mappings[field.type]
            const availableColumns = getAvailableColumns(tableMapping.tableId, field.type)
            const isLocked = currentMapping ? isColumnLocked(tableMapping.tableId, currentMapping) : false

            return (
              <div key={field.type} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ flex: '0 0 200px' }}>
                  <Space>
                    <Text strong>{field.label || field.type}</Text>
                    {field.required && <Tag color="red">Required</Tag>}
                    {isLocked && <LockOutlined style={{ color: '#8c8c8c' }} />}
                  </Space>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                    {field.description}
                  </div>
                </div>

                <div style={{ flex: '1 1 300px' }}>
                  <Select
                    value={currentMapping || undefined}
                    onChange={(value) =>
                      handleTableFieldMappingChange(tableMapping.tableId, field.type, value || null)
                    }
                    style={{ width: '100%' }}
                    placeholder="Select vendor column"
                    allowClear={!isLocked}
                    showSearch
                    disabled={isLocked}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={availableColumns.map((col) => ({
                      label: col,
                      value: col,
                    }))}
                  />
                  {isLocked && currentMapping && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                      <LockOutlined /> Locked (set in "{getLockSourceTableName(currentMapping)}")
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </Space>
      ),
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading vendor file sample data...</Text>
        </div>
      </div>
    )
  }

  const { valid } = validateMappings()

  // Filter out tables with no data rows
  const validTables = tableFieldMappings.filter((table) => table.totalRows > 0)
  const emptyTables = tableFieldMappings.filter((table) => table.totalRows === 0)

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Instructions */}
      <Card>
        <Title level={4}>Step 3: Map Fields Per Table</Title>
        <Text>
          Map standard field types to columns from your vendor file tables. Each table shows its own
          field mappings with sample data preview. Once a vendor column is mapped to a standard field
          in any table, it will be locked to that same field across all other tables.
        </Text>
      </Card>

      {/* Catalog Mappings - Kept for reference */}
      <Card title="Catalog File Mapping" style={{ background: '#fafafa' }} size="small">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {CATALOG_FIELDS.map((field) => {
            const mapping = catalogMappings.find((m) => m.fieldType === field.type)
            return (
              <div key={field.type} style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ flex: '0 0 200px' }}>
                  <Space>
                    <Text strong>{field.label || field.type}</Text>
                    {field.required && <Tag color="red">Required</Tag>}
                  </Space>
                </div>
                <div style={{ flex: 1 }}>
                  <Select
                    value={mapping?.selectedColumns[0] || undefined}
                    onChange={(column) =>
                      handleCatalogMappingChange(field.type, column ? [column] : [])
                    }
                    style={{ width: '100%' }}
                    placeholder="Select column from your file"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={state.catalogColumns.map((col) => ({
                      label: col,
                      value: col,
                    }))}
                  />
                </div>
              </div>
            )
          })}
        </Space>
      </Card>

      {/* Empty tables warning */}
      {emptyTables.length > 0 && (
        <Alert
          type="info"
          message="Empty Tables Skipped"
          description={
            <div>
              The following tables have no data rows and will be skipped:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {emptyTables.map((table) => (
                  <li key={table.tableId}>
                    {table.tableId} (from "{table.tabName}")
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}

      {/* Vendor Mappings - Per Table Accordion */}
      <Card title="Vendor File Mapping (Per Table)" style={{ background: '#fafafa' }}>
        {validTables.length === 0 ? (
          <Alert
            type="warning"
            message="No tables with data"
            description="All tables in the vendor file are empty. Please upload a different file."
          />
        ) : (
          <Collapse
            activeKey={activeKeys}
            onChange={(keys) => setActiveKeys(keys as string[])}
            items={validTables.map(buildTableItem).filter((item): item is NonNullable<typeof item> => item !== null)}
          />
        )}
      </Card>

      {/* Action buttons */}
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button size="large" onClick={onBack}>
              Back
            </Button>
            <Button size="large" onClick={onCancel}>
              Cancel
            </Button>
          </Space>
          <Button type="primary" size="large" onClick={handleNext} disabled={!valid || validTables.length === 0}>
            Next: Configure Pricing
          </Button>
        </Space>
      </Card>

      {/* Sample Data Modal */}
      <SampleDataModal
        visible={sampleModalVisible}
        onClose={() => setSampleModalVisible(false)}
        tableId={sampleModalTableId}
        sampleData={sampleData}
        tableFieldMappings={tableFieldMappings}
      />
    </Space>
  )
}
